import { useState } from 'react';
import { Alert, Button, Skeleton, Typography } from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';
import { StatusPill } from '@/components/StatusPill';
import { colors } from '@/theme/tokens';
import { formatUsd, type RoleUsage, type RunPreview } from '@/api/runEvidence';

const { Text } = Typography;
const MONO = "'JetBrains Mono', monospace";

// ─────────────────────────────────────────────────────────────────────────────
// The run drawer's SI-03b sections (screen 06, revision 2): the scenarios a run would run with drafts marked
// Exploratory, how this engine runs them (one mode, one trial: said, not offered), and the cost from the server's
// preview — "Estimated priced portion" over the scenarios it can price, the unpriced roles named, never $0. Nothing
// here draws a control the server cannot honour: no mode switch, no repeat count, no cap approval.
// ─────────────────────────────────────────────────────────────────────────────

const MAX_LISTED = 6;

const ROLE_LABEL: Record<string, string> = { Agent: 'Agent', Peer: 'Peers', Router: 'Skill router', Other: 'Other calls' };

export function ScenariosToRun({
  preview,
  error,
  isDark,
}: {
  preview: RunPreview | null | undefined;
  error: string | null;
  isDark: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  if (preview === undefined) return <Skeleton active paragraph={{ rows: 3 }} title={false} />;
  if (preview === null)
    return (
      <Text type="secondary" style={{ fontSize: 12.5 }}>
        Which scenarios this run would include could not be read{error ? ` (${error})` : ''}. The server still runs the
        agent's active scenarios, or the one named.
      </Text>
    );
  if (preview.scenarios.length === 0)
    return <Alert type="warning" showIcon title="No active scenario to run" description="This agent has no active scenario. Nothing would run." />;

  const drafts = preview.scenarios.filter((s) => s.exploratory).length;
  // Collapsed, drafts come first so what runs as Exploratory is never hidden behind "Show all".
  const shown = showAll
    ? preview.scenarios
    : [...preview.scenarios.filter((s) => s.exploratory), ...preview.scenarios.filter((s) => !s.exploratory)].slice(0, MAX_LISTED);
  const rowBorder = isDark ? colors.slate[700] : colors.slate[200];
  return (
    <div>
      <div style={{ border: `1px solid ${rowBorder}`, borderRadius: 8, overflow: 'hidden' }} data-testid="scenarios-to-run">
        {shown.map((s, i) => (
          <div
            key={s.scenarioId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 10px',
              borderTop: i === 0 ? 'none' : `1px solid ${rowBorder}`,
              minWidth: 0,
            }}
          >
            <span style={{ flex: 1, minWidth: 0, fontFamily: MONO, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.title ?? s.scenarioId}>
              {s.scenarioId}
            </span>
            {s.exploratory ? (
              <StatusPill tone="violet">Draft → Exploratory</StatusPill>
            ) : (
              s.lifecycleStage && <Text type="secondary" style={{ fontSize: 11.5 }}>{s.lifecycleStage}</Text>
            )}
          </div>
        ))}
      </div>
      {preview.scenarios.length > MAX_LISTED && (
        <Button type="link" size="small" style={{ paddingLeft: 0 }} onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer' : `Show all ${preview.scenarios.length}`}
        </Button>
      )}
      {drafts > 0 && (
        <Alert
          type="info"
          showIcon
          icon={<ExperimentOutlined />}
          style={{ marginTop: 10 }}
          title={`${drafts} draft${drafts === 1 ? '' : 's'} will run as Exploratory`}
          description="Their results never count toward release. Approving a draft later does not promote these results; a new run after approval does."
        />
      )}
    </div>
  );
}

/** The engine's one mode and one trial, said plainly. Nothing to pick until the server can honour a choice. */
export function HowItRuns() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div>
        <StatusPill tone="ink">Real agent, isolated</StatusPill>
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
          The agent runs through the production path, with its skills, knowledge, patient brief and peers, against each
          scenario's patient. This is the only mode this engine runs: contract-checks-only and staging integration are not
          available yet.
        </Text>
      </div>
      <div>
        <StatusPill tone="slate">Once per scenario</StatusPill>
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
          Repeated trials, which show variation such as “2 of 3 passed”, are not built yet. Until then a
          rerun is a new run, and earlier results stay as they are.
        </Text>
      </div>
    </div>
  );
}

function roleLine(r: RoleUsage): string {
  const names = r.deployments.map((d) => d.deployment ?? 'unrecorded').join(', ');
  return `${ROLE_LABEL[r.role] ?? r.role} · ${names || 'unrecorded'}`;
}

export function CostSection({ preview, isDark }: { preview: RunPreview | null | undefined; isDark: boolean }) {
  if (preview === undefined) return <Skeleton active paragraph={{ rows: 2 }} title={false} />;
  if (preview === null)
    return (
      <Text type="secondary" style={{ fontSize: 12.5 }}>
        No estimate: what this run would do could not be read. A run's cost is recorded as it happens; unpriced calls are
        counted, never added as $0.
      </Text>
    );
  const e = preview.estimate;
  const rowBorder = isDark ? colors.slate[700] : colors.slate[200];
  const unpricedRoles = e.latestRun.filter((r) => r.unpricedCalls > 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="run-cost">
      {e.latestRun.length > 0 && (
        <div>
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            Who made calls in run #{e.latestRunId}, the agent's latest run with recorded usage
          </Text>
          {e.latestRun.map((r) => (
            <div
              key={r.role}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', borderBottom: `1px solid ${rowBorder}` }}
            >
              <span style={{ minWidth: 0 }}>
                <Text style={{ fontSize: 12.5 }}>{roleLine(r)}</Text>
                <Text type="secondary" style={{ display: 'block', fontSize: 11.5 }}>
                  {r.calls} call{r.calls === 1 ? '' : 's'}
                  {r.unpricedCalls > 0 ? ` · ${r.unpricedCalls} with no rate on file` : ''}
                </Text>
              </span>
              <span style={{ flexShrink: 0 }}>
                {r.pricedCostUsd != null && r.unpricedCalls === 0 ? (
                  <Text style={{ fontSize: 12.5 }}>{formatUsd(r.pricedCostUsd)}</Text>
                ) : r.pricedCostUsd != null ? (
                  <Text style={{ fontSize: 12.5 }}>{formatUsd(r.pricedCostUsd)} + unpriced</Text>
                ) : (
                  <StatusPill tone="slate">Price unavailable</StatusPill>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
        <span>
          <Text strong style={{ fontSize: 13 }}>Estimated priced portion</Text>
          <Text type="secondary" style={{ display: 'block', fontSize: 11.5 }}>
            {e.estimated > 0
              ? `${e.estimated} of ${e.scenarios} scenario${e.scenarios === 1 ? '' : 's'}, from their last ${e.resultsRead} recorded result${e.resultsRead === 1 ? '' : 's'}`
              : 'No scenario here has a fully priced earlier result'}
            {unpricedRoles.length > 0 &&
              ` · ${unpricedRoles.map((r) => ROLE_LABEL[r.role] ?? r.role).join(' and ')} cost unavailable: not priced and not counted as $0`}
          </Text>
        </span>
        <Text strong style={{ fontSize: 14, flexShrink: 0 }} data-testid="estimated-priced-portion">
          {e.lowUsd != null && e.highUsd != null
            ? e.lowUsd === e.highUsd
              ? formatUsd(e.lowUsd)
              : `${formatUsd(e.lowUsd)} – ${formatUsd(e.highUsd)}`
            : 'No estimate'}
        </Text>
      </div>
      {e.notes.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, color: isDark ? colors.slate[400] : colors.slate[500] }}>
          {e.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
